import { QUANTIDADE_DE_CODIGOS_DE_RECUPERACAO, TOTP_DIGITOS } from '@locaveri/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Copy, LoaderCircle, Printer, ShieldCheck } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { AuthLayout } from '@/components/auth/auth-layout'
import { CampoDeCodigo } from '@/components/auth/campo-de-codigo'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { confirmarTotp, iniciarSetupTotp } from '@/features/auth/api'
import { errorMessage } from '@/lib/api'
import { encerrarSessao } from '@/lib/sessao'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Configuração obrigatória do 2FA para papel administrativo.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTA TELA NÃO TEM BOTÃO "AGORA NÃO"
 * ---------------------------------------------------------------------------
 * O painel mostra onde clientes de alto patrimônio estiveram, com placa,
 * horário e endereço. Senha sozinha não sustenta esse acesso, e "configuro
 * depois" nunca chega. Quem não quiser configurar agora tem uma saída honesta:
 * sair da sessão. O que não existe é entrar sem o segundo fator.
 *
 * Os códigos de recuperação aparecem UMA vez. O servidor guarda só o hash de
 * cada um — não existe "mostrar de novo", e é isso que faz o aviso desta tela
 * ser verdadeiro em vez de decorativo.
 */

type Etapa = 'configurar' | 'codigos'

export function Configurar2faPage(): ReactElement {
  const navigate = useNavigate()
  const definirSessao = useAuthStore((estado) => estado.definirSessao)
  // Setup OBRIGATÓRIO (primeiro login de admin, sem sessão): o desafio do
  // login é a credencial das duas chamadas. CONGELADO na montagem: a
  // confirmação limpa o store, e um valor vivo aqui trocaria a chave da query
  // no meio do sucesso — o refetch responderia "já está ativa" e cobriria a
  // tela dos códigos de recuperação com um erro (aconteceu em 03/09/2026).
  const [desafioDoSetup] = useState(() => useAuthStore.getState().setupPendente?.desafioId)

  const [etapa, setEtapa] = useState<Etapa>('configurar')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [guardou, setGuardou] = useState(false)

  const setup = useQuery({
    queryKey: ['auth', '2fa', 'setup', desafioDoSetup ?? 'sessao'],
    queryFn: () => iniciarSetupTotp(desafioDoSetup),
    // O segredo é gerado uma vez por visita: refazer a consulta no meio da
    // configuração trocaria o QR que a pessoa acabou de escanear.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const confirmar = useMutation({
    mutationFn: (codigoTotp: string) => confirmarTotp(codigoTotp, desafioDoSetup),
    onSuccess: (sessao) => {
      // A API reemite a sessão com `totp: true`; sem isso o token continuaria
      // dizendo que este acesso não passou pelo segundo fator.
      definirSessao(sessao)
      setEtapa('codigos')
    },
    onError: (causa: unknown) => {
      setErro(errorMessage(causa))
      setCodigo('')
    },
  })

  function confirmarCodigo(valor: string): void {
    setErro(null)

    if (valor.length !== TOTP_DIGITOS) {
      setErro(`Digite os ${String(TOTP_DIGITOS)} dígitos gerados pelo aplicativo.`)
      return
    }

    confirmar.mutate(valor)
  }

  async function copiar(texto: string, mensagem: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(texto)
      toast.success(mensagem)
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.')
    }
  }

  if (setup.isPending) {
    return (
      <AuthLayout titulo="Configurar verificação em duas etapas" largura="larga">
        <LoadingState rotulo="Gerando o segredo do autenticador…" linhas={2} />
      </AuthLayout>
    )
  }

  if (setup.isError) {
    return (
      <AuthLayout titulo="Configurar verificação em duas etapas" largura="larga">
        <ErrorState
          titulo="Não foi possível iniciar a configuração"
          erro={setup.error}
          aoTentarNovamente={() => {
            void setup.refetch()
          }}
        />
      </AuthLayout>
    )
  }

  const { otpauthUrl, segredoBase32, codigosDeRecuperacao } = setup.data

  if (etapa === 'codigos') {
    return (
      <AuthLayout
        titulo="Guarde seus códigos de recuperação"
        descricao="Eles são a sua única entrada se você perder o celular."
        largura="larga"
      >
        <Alert variant="warning">
          <AlertTitle>Esta lista aparece uma única vez</AlertTitle>
          <AlertDescription>
            O servidor guarda apenas o hash de cada código — nem o suporte consegue mostrá-los de
            novo. Cada código serve para uma entrada só.
          </AlertDescription>
        </Alert>

        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {codigosDeRecuperacao.map((item) => (
            <li
              key={item}
              className="rounded-lg border border-border bg-elevated px-3 py-2 text-center
                text-sm tabular-money tracking-wider"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              void copiar(
                codigosDeRecuperacao.join('\n'),
                `${String(QUANTIDADE_DE_CODIGOS_DE_RECUPERACAO)} códigos copiados.`,
              )
            }}
          >
            <Copy aria-hidden="true" />
            Copiar todos
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              window.print()
            }}
          >
            <Printer aria-hidden="true" />
            Imprimir
          </Button>
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-sm text-fg-secondary">
          <Checkbox
            checked={guardou}
            onChange={(evento) => {
              setGuardou(evento.target.checked)
            }}
            className="mt-0.5"
          />
          <span>Guardei os códigos em um lugar seguro, fora deste computador.</span>
        </label>

        <Button
          className="w-full"
          disabled={!guardou}
          onClick={() => {
            void navigate({ to: '/' })
          }}
        >
          Ir para o painel
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      titulo="Configurar verificação em duas etapas"
      descricao="Obrigatória para todos os papéis internos da Locaveri."
      largura="larga"
      rodape={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void encerrarSessao().then(() => navigate({ to: '/login' }))
          }}
        >
          Sair sem configurar
        </Button>
      }
    >
      <ol className="space-y-6">
        <li className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            1. Escaneie o QR Code no seu aplicativo autenticador
          </p>

          <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            {/* Fundo branco fixo: leitor de QR precisa de contraste claro, e no
                tema escuro um QR "temático" simplesmente não é lido. */}
            <div className="rounded-lg bg-white p-3">
              <QRCodeSVG value={otpauthUrl} size={160} bgColor="#FFFFFF" fgColor="#000000" />
            </div>

            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm text-fg-secondary">
                Não consegue escanear? Cadastre o segredo manualmente:
              </p>
              <p
                className="break-all rounded-lg border border-border bg-elevated px-3 py-2
                text-sm tabular-money tracking-wider"
              >
                {segredoBase32}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void copiar(segredoBase32, 'Segredo copiado.')
                }}
              >
                <Copy aria-hidden="true" />
                Copiar segredo
              </Button>
            </div>
          </div>
        </li>

        <li className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            2. Digite o código de {TOTP_DIGITOS} dígitos que o aplicativo mostrar
          </p>

          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <CampoDeCodigo
            rotulo="Código do aplicativo autenticador"
            valor={codigo}
            aoMudar={setCodigo}
            aoCompletar={confirmarCodigo}
            quantidade={TOTP_DIGITOS}
            desabilitado={confirmar.isPending}
            invalido={erro !== null}
          />

          <Button
            className="w-full"
            disabled={confirmar.isPending}
            onClick={() => {
              confirmarCodigo(codigo)
            }}
          >
            {confirmar.isPending ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <ShieldCheck aria-hidden="true" />
            )}
            {confirmar.isPending ? 'Confirmando…' : 'Ativar verificação em duas etapas'}
          </Button>
        </li>
      </ol>
    </AuthLayout>
  )
}
